/**
 * The rest of the way, inside the cabinet (owner, 2026-10-29): Bybit, then
 * Telegram, then the start. Each step opens once the one before it is done,
 * read from the same panels the cabinet uses, so a step done elsewhere counts
 * here too. Telegram may be skipped; the rest may not.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/design-system/Button";
import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { BybitConnectionPanel } from "../identity/BybitConnectionPanel";
import { StartControl, useTradingControl } from "../identity/StartControl";
import { TelegramPanel } from "../notifications/TelegramPanel";
import { getBybitConnection, readTelegramLink } from "@/shared/api";

import { Steps } from "@/shared/onboarding/Steps";

const SKIP_KEY = "skryptos.onboarding.telegram-skipped";

function skipped(): boolean {
  try {
    return window.localStorage.getItem(SKIP_KEY) === "yes";
  } catch {
    return false;
  }
}

/** Which step the account is on: 2 Bybit, 3 Telegram, 4 start, 5 done. */
export function onboardingStep(state: {
  readonly bybit: boolean; readonly telegram: boolean; readonly skippedTelegram: boolean; readonly trading: boolean;
}): number {
  if (!state.bybit) return 2;
  if (!state.telegram && !state.skippedTelegram) return 3;
  if (!state.trading) return 4;
  return 5;
}

export function useOnboarding(tenantId: string, accountId: string): { readonly step: number | null; readonly skip: () => void } {
  const [skippedTelegram, setSkipped] = useState(skipped);
  const bybit = useQuery({
    queryKey: ["bybit-connection", tenantId, accountId],
    queryFn: async () => {
      const { data } = await getBybitConnection({ credentials: "same-origin", cache: "no-store" });
      if (data === undefined) throw new Error("connection unavailable");
      return data;
    },
    refetchInterval: 5000,
  });
  const telegram = useQuery({
    queryKey: ["notifications", "telegram"],
    retry: false,
    refetchInterval: 5000,
    queryFn: async () => {
      const { data } = await readTelegramLink();
      if (data === undefined) throw new Error("telegram status unavailable");
      return data;
    },
  });
  const control = useTradingControl(tenantId, accountId);
  const known = bybit.data !== undefined && telegram.data !== undefined && control.data !== undefined;
  return {
    step: !known
      ? null
      : onboardingStep({
          bybit: bybit.data.status === "CONNECTED" || bybit.data.status === "LEGACY_CONNECTED",
          telegram: telegram.data.linked,
          skippedTelegram,
          trading: control.data.admissions_enabled,
        }),
    skip: () => {
      try {
        window.localStorage.setItem(SKIP_KEY, "yes");
      } catch {
        // Not remembered across visits; skipped for now all the same.
      }
      setSkipped(true);
    },
  };
}

function BybitGuide(): React.JSX.Element {
  return (
    <Panel title="Как создать ключ Bybit" testId="onboarding-bybit-guide">
      <ol className="flex list-decimal flex-col gap-2 pl-5 text-body-main text-text-secondary">
        <li>Войдите на bybit.com → значок профиля → <b className="text-text-primary">API</b> → <b className="text-text-primary">Создать новый ключ</b> → «Ключи API, созданные системой».</li>
        <li>Назначение — «Транзакция по API». Название — любое, например Skryptos.</li>
        <li>Разрешения: <b className="text-text-primary">Чтение и запись</b>; в разделе «Единый торговый аккаунт» отметьте <b className="text-text-primary">Ордера и Позиции</b> по контрактам.</li>
        <li><b className="text-status-negative">Вывод средств не включайте.</b> Бот не может и не должен выводить деньги.</li>
        <li>Подтвердите кодом и скопируйте API Key и Secret — Secret Bybit показывает один раз. Вставьте их ниже.</li>
      </ol>
    </Panel>
  );
}

export function StartPage({ tenantId, accountId, roles }: {
  readonly tenantId: string; readonly accountId: string; readonly roles: readonly string[];
}): React.JSX.Element {
  const { step, skip } = useOnboarding(tenantId, accountId);
  const control = useTradingControl(tenantId, accountId);
  return (
    <div className="flex max-w-3xl flex-col gap-3" data-testid="onboarding">
      <PageHeader title="Подключение" description="Осталось несколько шагов — по одному за раз." />
      <Panel>
        <Steps current={step ?? 2} />
      </Panel>
      {step === null ? (
        <Panel><p role="status" className="text-body-main text-text-secondary">Проверяю, что уже подключено…</p></Panel>
      ) : step === 2 ? (
        <>
          <BybitGuide />
          <BybitConnectionPanel tenantId={tenantId} accountId={accountId} roles={roles} />
        </>
      ) : step === 3 ? (
        <>
          <TelegramPanel owner={roles.includes("OWNER")} />
          <Panel>
            <div className="flex flex-col gap-2 text-body-main text-text-secondary">
              <p>В Telegram приходят открытия и закрытия сделок, прибыль за день и всё, что требует вашего внимания. Рекомендуем подключить.</p>
              <div><Button type="button" onClick={skip}>Пропустить пока</Button></div>
            </div>
          </Panel>
        </>
      ) : step === 4 ? (
        <Panel title="Запуск" testId="onboarding-start">
          <div className="flex flex-col gap-3 text-body-main text-text-secondary">
            <p>Всё готово. После запуска бот сам выбирает монеты, открывает сделки и ведёт их до закрытия. Остановить можно в любой момент — открытые сделки он всё равно доведёт.</p>
            {control.data !== undefined && (
              <StartControl tenantId={tenantId} accountId={accountId} control={control.data} onChanged={async () => { await control.refetch(); }} />
            )}
          </div>
        </Panel>
      ) : (
        <Panel title="Готово" testId="onboarding-done">
          <div className="flex flex-col gap-3 text-body-main text-text-secondary">
            <p>Бот торгует. Первые сделки появятся, когда монеты пройдут отбор.</p>
            <Link className="cursor-pointer text-text-primary underline underline-offset-2" to="/app">Перейти в кабинет</Link>
          </div>
        </Panel>
      )}
    </div>
  );
}
